import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FixaFormField } from "@coworker/apprestructured/src/shared/wrappers/FixaFormField/FixaFormField";
import { FixaInputField } from "@coworker/apprestructured/src/shared/wrappers/FixaInputField/FixaInputField";
import { FixaTextArea } from "@coworker/apprestructured/src/shared/wrappers/FixaTextArea/FixaTextArea";
import { useCallback, useState } from "react";
import { CreateTaskTypeDTO, TaskType } from "../../types/TaskType";

const TEXT_AREA_ROW_COUNT = 15 as const;

export interface ErrorMessageAndException {
  message: string;
  exception: unknown;
}

interface TaskTypeFormProps<T> {
  readOnly: boolean;
  onSave?(errors: ErrorMessageAndException[], taskType: T): Promise<boolean>;
  taskTypeValues: T;
  buttonText?: string;
}

function countRowsInString(str: string) {
  return str.split("\n").length;
}

function isTaskType(object: CreateTaskTypeDTO | TaskType): object is TaskType {
  return "id" in object && "close_reason_ids" in object;
}

export default function TaskTypeForm<T extends CreateTaskTypeDTO>({
  readOnly: readonly,
  onSave,
  taskTypeValues,
  buttonText,
}: TaskTypeFormProps<T>) {
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const [name, setName] = useState(taskTypeValues?.name);
  const [label, setLabel] = useState(taskTypeValues?.label);
  const [creatorFragments, setCreatorFragments] = useState(
    JSON.stringify(taskTypeValues?.creator_fragments, null, 2) ?? ""
  );
  const [titleCreator, setTitleCreator] = useState(
    JSON.stringify(taskTypeValues?.title_creator, null, 2) ?? ""
  );
  const [viewerTopFragment, setViewerTopFragment] = useState(
    JSON.stringify(taskTypeValues?.viewer_top_fragment, null, 2) ?? ""
  );
  const [viewerFragments, setViewerFragments] = useState(
    JSON.stringify(taskTypeValues?.viewer_fragments, null, 2) ?? ""
  );
  const [cardFragments, setCardFragments] = useState(
    JSON.stringify(taskTypeValues?.card_fragments, null, 2) ?? ""
  );
  const [closeReasonIds, setCloseReasonIds] = useState(
    JSON.stringify(taskTypeValues?.close_reason_ids, null, 2) ?? ""
  );
  const [details, setDetails] = useState(
    JSON.stringify(taskTypeValues?.details, null, 2) ?? ""
  );
  const [afterCreate, setAfterCreate] = useState(
    JSON.stringify(taskTypeValues?.after_create, null, 2) || "null"
  );
  const [afterComplete, setAfterComplete] = useState(
    JSON.stringify(taskTypeValues?.after_complete, null, 2) || "null"
  );
  const [notificationsConfig, setNotificationsConfig] = useState(
    JSON.stringify(taskTypeValues?.notifications_config, null, 2) ?? ""
  );

  const handleSaveClick = useCallback(() => {
    async function fn() {
      setSaving(true);
      const errors: ErrorMessageAndException[] = [];

      const taskType: T = {
        ...taskTypeValues,
        name,
        label,
      };

      try {
        taskType.creator_fragments = JSON.parse(creatorFragments);
      } catch (exception) {
        errors.push({
          message: "Failed to parse creator_fragments JSON",
          exception,
        });
      }

      try {
        taskType.title_creator = JSON.parse(titleCreator);
      } catch (exception) {
        errors.push({
          message: "Failed to parse title_creator JSON",
          exception,
        });
      }

      try {
        taskType.viewer_top_fragment = JSON.parse(viewerTopFragment);
      } catch (exception) {
        errors.push({
          message: "Failed to parse viewer_top_fragment JSON",
          exception,
        });
      }

      try {
        taskType.viewer_fragments = JSON.parse(viewerFragments);
      } catch (exception) {
        errors.push({
          message: "Failed to parse viewer_fragments JSON",
          exception,
        });
      }

      try {
        taskType.card_fragments = JSON.parse(cardFragments);
      } catch (exception) {
        errors.push({
          message: "Failed to parse card_fragments JSON",
          exception,
        });
      }

      try {
        taskType.close_reason_ids = JSON.parse(closeReasonIds);
      } catch (exception) {
        errors.push({
          message: "Failed to parse close_reason_ids JSON",
          exception,
        });
      }

      try {
        taskType.details = JSON.parse(details);
      } catch (exception) {
        errors.push({ message: "Failed to parse details JSON", exception });
      }

      try {
        taskType.after_create = JSON.parse(afterCreate);
      } catch (exception) {
        errors.push({
          message: "Failed to parse after_create JSON",
          exception,
        });
      }

      try {
        taskType.after_complete = JSON.parse(afterComplete);
      } catch (exception) {
        errors.push({
          message: "Failed to parse after_complete JSON",
          exception,
        });
      }

      try {
        taskType.notifications_config = JSON.parse(notificationsConfig);
      } catch (exception) {
        errors.push({
          message: "Failed to parse notifications_config JSON",
          exception,
        });
      }

      const success = onSave ? await onSave(errors, taskType) : true;
      setSuccess(success);

      setSaving(false);
    }
    fn();
  }, [
    taskTypeValues,
    name,
    label,
    onSave,
    creatorFragments,
    titleCreator,
    viewerTopFragment,
    viewerFragments,
    cardFragments,
    closeReasonIds,
    details,
    afterCreate,
    afterComplete,
    notificationsConfig,
  ]);

  return (
    <>
      {isTaskType(taskTypeValues) && (
        <>
          <FixaFormField>
            <FixaInputField
              id="id"
              type="text"
              label="id"
              value={`${taskTypeValues.id}`}
              readOnly={true}
            />
          </FixaFormField>

          <FixaFormField>
            <FixaInputField
              id="created_at"
              type="text"
              label="created_at"
              value={taskTypeValues.created_at}
              readOnly={true}
            />
          </FixaFormField>

          <FixaFormField>
            <FixaInputField
              id="updated_at"
              type="text"
              label="updated_at"
              value={taskTypeValues.updated_at}
              readOnly={true}
            />
          </FixaFormField>
        </>
      )}

      <FixaFormField>
        <FixaInputField
          id="name"
          type="text"
          label="name"
          value={name}
          readOnly={readonly}
          onChange={(e) => setName(e.target.value)}
        />
      </FixaFormField>

      <FixaFormField>
        <FixaInputField
          id="label"
          type="text"
          label="label"
          value={label}
          readOnly={readonly}
          onChange={(e) => setLabel(e.target.value)}
        />
      </FixaFormField>

      <FixaFormField>
        <FixaTextArea
          id="title_creator"
          label="title_creator"
          value={titleCreator}
          readOnly={readonly}
          rows={
            readonly ? countRowsInString(titleCreator) : TEXT_AREA_ROW_COUNT
          }
          onChange={(e) => setTitleCreator(e.target.value)}
        />
      </FixaFormField>

      <FixaFormField>
        <FixaTextArea
          id="creator_fragments"
          label="creator_fragments"
          value={creatorFragments}
          readOnly={readonly}
          rows={
            readonly ? countRowsInString(creatorFragments) : TEXT_AREA_ROW_COUNT
          }
          onChange={(e) => setCreatorFragments(e.target.value)}
        />
      </FixaFormField>

      <FixaFormField>
        <FixaTextArea
          id="viewer_top_fragment"
          label="viewer_top_fragment"
          value={viewerTopFragment}
          readOnly={readonly}
          rows={
            readonly
              ? countRowsInString(viewerTopFragment)
              : TEXT_AREA_ROW_COUNT
          }
          onChange={(e) => setViewerTopFragment(e.target.value)}
        />
      </FixaFormField>

      <FixaFormField>
        <FixaTextArea
          id="viewer_fragments"
          label="viewer_fragments"
          value={viewerFragments}
          readOnly={readonly}
          rows={
            readonly ? countRowsInString(viewerFragments) : TEXT_AREA_ROW_COUNT
          }
          onChange={(e) => setViewerFragments(e.target.value)}
        />
      </FixaFormField>

      <FixaFormField>
        <FixaTextArea
          id="card_fragments"
          label="card_fragments"
          value={cardFragments}
          readOnly={readonly}
          rows={
            readonly ? countRowsInString(cardFragments) : TEXT_AREA_ROW_COUNT
          }
          onChange={(e) => setCardFragments(e.target.value)}
        />
      </FixaFormField>

      <FixaFormField>
        <FixaInputField
          id="close_reason_ids"
          type="text"
          label="close_reason_ids"
          value={closeReasonIds}
          readOnly={readonly}
          onChange={(e) => setCloseReasonIds(e.target.value)}
        />
      </FixaFormField>

      <FixaFormField>
        <FixaTextArea
          id="details"
          label="details"
          value={details}
          readOnly={readonly}
          rows={readonly ? countRowsInString(details) : TEXT_AREA_ROW_COUNT}
          onChange={(e) => setDetails(e.target.value)}
        />
      </FixaFormField>

      <FixaFormField>
        <FixaTextArea
          id="notifications_config"
          label="notifications_config"
          value={notificationsConfig}
          readOnly={readonly}
          rows={
            readonly
              ? countRowsInString(notificationsConfig)
              : TEXT_AREA_ROW_COUNT
          }
          onChange={(e) => setNotificationsConfig(e.target.value)}
        />
      </FixaFormField>

      <FixaFormField>
        <FixaTextArea
          id="after_create"
          label="after_create"
          value={afterCreate}
          readOnly={readonly}
          rows={readonly ? countRowsInString(afterCreate) : TEXT_AREA_ROW_COUNT}
          onChange={(e) => setAfterCreate(e.target.value)}
        />
      </FixaFormField>

      <FixaFormField>
        <FixaTextArea
          id="after_complete"
          label="after_complete"
          value={afterComplete}
          readOnly={readonly}
          rows={
            readonly ? countRowsInString(afterComplete) : TEXT_AREA_ROW_COUNT
          }
          onChange={(e) => setAfterComplete(e.target.value)}
        />
      </FixaFormField>

      {!readonly && (
        <FixaButton
          onClick={handleSaveClick}
          text={buttonText}
          fluid
          type="primary"
          loading={saving}
          disabled={success}
          htmlType="button"
        />
      )}
    </>
  );
}
